import { AppState, type AppStateStatus } from "@/platform/app-state";
import { possuiTokenDeSessao } from "./Api";
import { possuiSessaoPrestador } from "./ApiPrestador";
import { drenarDiarias, temDiariaParaEnviar } from "./PrestadorDiaria";
import { SyncService } from "./Sync";

/**
 * NOVA TENTATIVA AUTOMÁTICA de envio com o app ABERTO.
 *
 * Por que existe (relato de 30/09/2026): o dreno só rodava em gestos e entradas de tela.
 * Voltar do segundo plano NÃO drenava, e o "voltou a internet" era medido pelo Google — na
 * rede de órgão público que deixa o Google passar e bloqueia os nossos hosts, o app nunca
 * se via offline, então ao sair de lá não havia transição nenhuma para disparar o envio.
 * A batida ficava parada até alguém puxar a tela de Justificativas.
 *
 * Regras:
 *  - só age com o app em primeiro plano (em segundo plano é a tarefa de
 *    services/SyncSegundoPlano.ts);
 *  - só gasta rede quando há algo pendente (`temEnvioPendente` é leitura local);
 *  - com pendência que não sai, espaça: 1 min → 2 min → 5 min (e fica em 5);
 *  - ao voltar do segundo plano, tenta logo e zera o espaçamento.
 * O `processOfflineQueue` tem a própria trava de reentrância — disparar junto com os
 * outros gatilhos do app é seguro.
 *
 * Vale para as DUAS sessões do aparelho: a do colaborador (fila do Sync) e a do
 * PRESTADOR (diárias do PrestadorDiaria — relato de 01/10/2026: a diária também só subia
 * quando a pessoa abria a tela). O `drenarDiarias` também é um envio por vez.
 */
const ESPERAS_COM_PENDENCIA_MS = [60_000, 2 * 60_000, 5 * 60_000];
const ESPERA_SEM_PENDENCIA_MS = 5 * 60_000;

let iniciado = false;
let timer: ReturnType<typeof setTimeout> | null = null;
let nivel = 0;
let rodando = false;

const agendar = (ms: number) => {
  if (timer) clearTimeout(timer);
  timer = setTimeout(() => {
    timer = null;
    void ciclo();
  }, ms);
};

// "unknown" (iOS antes do 1º evento) conta como primeiro plano: exigir "active" ali faria o
// primeiro ciclo sair sem reagendar e o agendador ficar parado até a próxima troca de estado.
const emPrimeiroPlano = () => {
  const estado = AppState.currentState;
  return estado !== "background" && estado !== "inactive";
};

async function ciclo(): Promise<void> {
  if (!emPrimeiroPlano() || rodando) return;
  rodando = true;
  let proxima = ESPERA_SEM_PENDENCIA_MS;
  try {
    const colaborador = await possuiTokenDeSessao();
    const prestador = await possuiSessaoPrestador();
    if (!colaborador && !prestador) return;
    const pendColaborador =
      colaborador && (await SyncService.temEnvioPendente());
    const pendPrestador = prestador && (await temDiariaParaEnviar());
    if (!pendColaborador && !pendPrestador) {
      nivel = 0;
      return;
    }
    if (pendColaborador) await SyncService.processOfflineQueue();
    if (pendPrestador) await drenarDiarias();
    const aindaPendente =
      (colaborador && (await SyncService.temEnvioPendente())) ||
      (prestador && (await temDiariaParaEnviar()));
    if (aindaPendente) {
      proxima = ESPERAS_COM_PENDENCIA_MS[nivel];
      nivel = Math.min(nivel + 1, ESPERAS_COM_PENDENCIA_MS.length - 1);
    } else {
      nivel = 0;
    }
  } catch (e) {
    console.warn("[SyncAgendador] ciclo: erro (ignorado):", e);
  } finally {
    rodando = false;
    if (emPrimeiroPlano()) agendar(proxima);
  }
}

/** Liga o agendador (uma vez por processo) — chamado na raiz do app. */
export function iniciarAgendadorDeEnvio(): void {
  if (iniciado) return;
  iniciado = true;
  AppState.addEventListener("change", (estado: AppStateStatus) => {
    if (estado === "active") {
      nivel = 0;
      // Pequena folga para o app assentar (o AuthContext refaz o /iniciar no mesmo evento).
      agendar(1_500);
    } else if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  });
  // WEB: o browser avisa quando a conexão VOLTA (`online`) — gatilho a mais, além do
  // AppState (visibilitychange/pageshow pelo shim). Mesma reação da volta ao primeiro plano:
  // zera o espaçamento e tenta logo. `navigator.onLine` nunca PROVA rede (rede de órgão
  // público, captive portal): é só uma tentativa antecipada — o `processOfflineQueue` decide.
  if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
    window.addEventListener("online", () => {
      if (!emPrimeiroPlano()) return;
      nivel = 0;
      agendar(1_500);
    });
  }
  agendar(5_000);
}
